import { useEffect, useRef } from "react";
import Header from "../Header";
import Footer from "../Footer";
import { INSTAGRAM_URL, LINE_URL } from "../config/socialLinks";
import { trackCtaClick } from "../utils/analytics";
import s from "./WebProductionPage.module.scss";

const features = [
  ["📱", "スマホ対応"],
  ["💬", "LINEへの導線"],
  ["📸", "Instagram連携"],
  ["📍", "Googleマップ・MEO対策"],
  ["✉️", "お問い合わせフォーム"],
  ["🔍", "基本SEO対策"],
  ["🌏", "多言語対応"],
  ["📅", "予約システム"],
  ["💳", "決済システム"],
];

const webProductionFaqs = [
  {
    question: "ほかのWeb制作会社との違いは何ですか？",
    answer: "制作会社によっては、文章や写真の簡単な変更だけでも、運用費・管理費が毎月発生します。長期的に運用すると大きな固定費になりますが、その費用に集客や予約など、成果につなげるための改善まで含まれているとは限りません。契約前に、これまでの具体的な成果や、月額費用に含まれる対応内容を詳しく確認することが大切です。私たちは、プロが品質を担保しながら、その固定費をいただかず、お客様自身で長期的に運用できる状態までつくります。",
    bullets: [
      "公開後の運用費・管理費は0円。Web制作業界でもほとんど例のない仕組みです",
      "誰でも操作しやすい管理画面を提供",
      "AIを使った更新・カスタマイズ方法まで全面的にサポート",
      "大手企業のWeb制作やサービス立ち上げ経験を持つプロが対応",
      "AI任せにせず、デザイン・SEO・予約導線・品質をプロが判断",
      "サイトの雰囲気に合わせた写真・映像素材も、プロカメラマンが撮影可能（別途料金）",
    ],
    note: "簡単な文章や写真の変更は、今の時代、AIを正しく使えば誰でもできるようになりました。だから毎月費用をいただくのではなく、お客様自身で自由に更新し、Webを育てられる仕組みと使い方をお渡しします。",
  },
  {
    question: "宮古島でホームページ制作を依頼すると、料金はいくらですか？",
    answer: "通常価格400,000円（税込）のところ、現在は残り1社様限定のキャンペーン価格200,000円（20万円・税込）です。ページ数が多い場合や、予約・決済など複雑な機能が必要な場合は、ヒアリング後に追加費用を含む正確なお見積もりをご提示します。",
  },
  {
    question: "公開後の運用費や管理費はかかりますか？",
    answer: "運用費・管理費は0円です。サーバーや外部サービスの利用状況によって実費が発生する場合はありますが、アクセス数が非常に多くなければ基本的に無料枠内で運用できる構成をご提案します。",
  },
  {
    question: "ホームページ公開後、自分で更新できますか？",
    answer: "はい。誰でも操作しやすい管理画面をご用意し、文章・写真・ブログ・SEO記事などを自分たちで更新できるようにします。AIを使った更新や改善方法も含め、長期的に自走できる体制づくりまでサポートします。",
  },
  {
    question: "すでにあるホームページのリニューアルも依頼できますか？",
    answer: "対応可能です。見た目を変えるだけではなく、SEO、情報設計、問い合わせ・予約導線、公開後の運用方法まで見直し、成果につながるWebサイトへ再設計します。",
  },
  {
    question: "ホームページはどのくらいの期間で完成しますか？",
    answer: "シンプルなホームページは通常1か月以内が目安です。写真や文章が揃い、デザイン確認が順調に進んだ場合は、最短1週間ほどで公開できる場合があります。",
  },
  {
    question: "宮古島の事業者向けSEO対策にも対応していますか？",
    answer: "対応しています。宮古島の地域名とサービス名を組み合わせた検索キーワードを設計し、ページ構成、タイトル、説明文、構造化データ、表示速度、予約までの導線をまとめて整えます。",
  },
  {
    question: "MEO対策・Googleマップ対策にも対応していますか？",
    answer: "対応しています。Googleビジネスプロフィールの基本情報、カテゴリ、営業時間、写真、サービス内容などを整理し、ホームページやSNSと情報を統一します。投稿・口コミ・写真を継続的に活用できる運用方法までご案内します。ただし、Googleマップの順位は競合や検索地点など複数の要因で変わるため、上位表示を保証するものではありません。",
  },
];

const cases = [
  {
    tag: "TOUR COMPANY",
    title: "オーストラリアのツアー会社",
    text: "Webサイトを全面リニューアルし、SEO・ツアーページ・予約導線を再設計。決済システムも導入し、予約受付から決済、予約完了メールの送信までを自動化。予約に関わる人件費や事務作業を無くし、入力漏れ・連絡漏れなどの人的ミスが起きない運用を構築しました。集客の90%以上を自社Web経由で獲得できる状態へ成長しています。",
    result: "予約業務を自動化し、外部手数料・人件費・事務作業を大幅削減｜年間1,000万円以上のコスト削減を実現",
    url: "https://mightyaussie.com/",
  },
  {
    tag: "PROMOTION LP",
    title: "New Balance様",
    text: "サッカースパイクの販売プロモーションLPを企画・制作。ユーザーが参加したくなる仕組みを組み込み、参加をきっかけにアクセスと商品への興味が広がるWeb体験を設計しました。スパイクの持つクールな世界観に合わせて、配色・タイポグラフィ・グラフィックを細部まで設計。商品の魅力とイメージを、この一ページで直感的に伝えられるデザインに仕上げました。",
    result: "ユーザー参加を起点に、商品認知と購買への関心を高めるプロモーションを実現",
    url: "https://web.playerapp.tokyo/newbalance-442",
  },
  {
    tag: "SPORTS PLATFORM",
    title: "Player! WHITE（プレイヤーホワイト）",
    text: "欧米のプロチームや学生スポーツのように、チーム専用アプリを簡単につくれるサービスを立ち上げ。チーム運営のデジタル化、ファンの醸成、資金集めを支援する仕組みを構築しました。リリース後も利用データやユーザーの声を継続的に収集・分析し、実際の利用状況に合わせて機能や使いやすさを改善。サービスの成長につながる企画・設計・改善を一貫して行いました。",
    result: "立ち上げ後、数多くのスポーツチームが導入",
    url: "https://ookami.tokyo/media-partner/",
  },
  {
    tag: "WEB APP",
    title: "Miyako Ocean Guide",
    text: "天気・風向き・風速・波・海の透明度・星空・サンセット・観光情報を一つに集約した、宮古島旅行者向けWebアプリを企画・開発・運用しています。その日の気象データをもとに、シュノーケルやビーチ遊びに適した場所と時間を分かりやすく案内。地点ごとのコンディション、3日間の予報、多言語表示、現地の声を共有できる投稿機能まで実装しています。公開後もアクセスデータや利用者の反応を確認しながら、旅行中に毎日使われるサービスを目指して継続的に改善しています。",
    result: "公開約1週間で1,000PVを達成",
    url: "https://premium-tour-miyako.com/ocean-guide/",
  },
  {
    tag: "TOUR WEBSITE",
    title: "宮古島ツアーサイト",
    text: "InstagramやThreadsなどのSNSからWebサイトへの流入をつくり、宮古島の撮影ツアー予約につなげる導線を設計。ツアー内容・料金・撮影作品・予約方法を一つのサイトに整理し、SNSで興味を持ったユーザーがサービスを詳しく理解し、そのまま問い合わせや予約へ進める構成にしています。観光情報やMiyako Ocean Guideからツアーを知ってもらう流れもつくり、SNS・検索・Webを組み合わせた自社集客の仕組みを構築しています。",
    result: "情報発信・SEO・直接予約を一つのサイトに集約",
    url: "https://premium-tour-miyako.com/",
  },
];

const Cta = ({ placement }) => (
  <div className={s.ctaButtons}>
    <a
      className={s.primaryCta}
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noreferrer"
      onClick={() =>
        trackCtaClick({
          label: "web_production_instagram",
          placement,
          destination: INSTAGRAM_URL,
          destinationType: "external_social",
        })
      }
    >
      Web制作依頼はこちら
      <span aria-hidden="true">→</span>
    </a>
    <a
      className={s.secondaryCta}
      href={LINE_URL}
      target="_blank"
      rel="noreferrer"
      onClick={() =>
        trackCtaClick({
          label: "web_production_line",
          placement,
          destination: LINE_URL,
          destinationType: "external_contact",
        })
      }
    >
      LINEで相談する
    </a>
  </div>
);

const WebProductionPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const sections = Array.from(page.querySelectorAll("section:not(:first-child)"));
    const cards = Array.from(page.querySelectorAll([
      `.${s.problemCard}`,
      `.${s.audienceCard}`,
      `.${s.featureCard}`,
      `.${s.caseCard}`,
      `.${s.productionFlowItem}`,
      `.${s.noteCard}`,
      `.${s.webFaqItem}`,
    ].join(",")));

    sections.forEach((element) => element.classList.add(s.revealSection));
    cards.forEach((element, index) => {
      element.classList.add(s.revealItem);
      element.style.setProperty("--reveal-delay", `${(index % 5) * 70}ms`);
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(s.isVisible);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8%" });

    [...sections, ...cards].forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Header logoColor="dark" />
      <main className={s.root} ref={pageRef}>
        <section className={s.hero}>
          <div className={s.heroGlowOne} />
          <div className={s.heroGlowTwo} />
          <div className={s.heroInner}>
            <p className={s.eyebrow}>MIYAKOJIMA WEB CREATIVE</p>
            <div className={s.heroClaims}>
              <span>宮古島最安級のWeb制作</span>
              <span>運用費・管理費 0円</span>
            </div>
            <h1>
              宮古島のWeb制作・
              <span>ホームページ制作</span>
            </h1>
            <p className={s.heroLead}>
              宮古島最安級の料金で、ツアー会社・飲食店・ホテルなど、地域の事業者に特化したWeb制作です。
              <br />
              SNSで知ってもらい、ホームページで詳しく伝え、予約・問い合わせにつなげる。
              <br />
              運用費・管理費は0円。作って終わりではなく、自分たちで育てられるホームページを制作します。
            </p>

            <div className={s.priceCard}>
              <div>
                <span>キャンペーン価格・残り1社様</span>
                <strong><small>¥</small>200,000<small>（税込）</small></strong>
                <em>通常価格 ¥400,000（税込）</em>
              </div>
              <div className={s.zeroCost}>
                <span>運用費</span><b>0円</b>
                <span>管理費</span><b>0円</b>
              </div>
            </div>
            <Cta placement="web_production_hero" />
            <p className={s.ctaNote}>まずはInstagram DMまたはLINEからお気軽にご相談ください。</p>
          </div>
        </section>

        <section className={s.problemSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>FOR LOCAL BUSINESS</p>
            <h2>こんなお悩みはありませんか？</h2>
            <div className={s.problemGrid}>
              {["SNSだけで集客している", "ホームページがない", "昔作ったHPがそのまま", "毎月の管理費が高い", "予約サイトの手数料が負担"].map((item) => (
                <div className={s.problemCard} key={item}><span>✓</span>{item}</div>
              ))}
            </div>
            <p className={s.centerCopy}>
              観光客が知りたい「料金・場所・内容・予約方法」を、迷わず一度で理解できるWebサイトに整理します。
            </p>
          </div>
        </section>

        <section className={s.audienceSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>FOR MIYAKOJIMA BUSINESS</p>
            <h2>宮古島で、お客様と直接つながる 事業者様のためのWeb制作です</h2>
            <div className={s.audienceGrid}>
              {[
                ["🏝️", "ツアー会社", "プラン・料金・予約方法を整理し、直接予約へ"],
                ["🍽️", "飲食店", "メニュー・営業時間・場所を検索から伝える"],
                ["🥂", "キャバクラ", "店舗の雰囲気・料金・求人・来店相談を分かりやすく"],
                ["🍸", "バー", "初めてのお客様にも安心できる情報と導線を"],
                ["☕", "カフェ", "写真・メニュー・Googleマップで来店につなげる"],
                ["🏨", "ホテル", "客室・設備・周辺情報を整理し、宿泊予約へつなげる"],
                ["🏡", "ヴィラ", "写真と滞在体験の魅力を伝え、直接予約を増やす"],
              ].map(([icon, title, text]) => (
                <article className={s.audienceCard} key={title}>
                  <span>{icon}</span><h3>{title}</h3><p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={s.premiseSection}>
          <div className={s.sectionInner}>
            <div className={s.premiseBox}>
              <p className={s.sectionLabel}>BEFORE WE START</p>
              <h2>前提として、Webはすぐには結果が出ません。</h2>
              <p>
                Webは時間をかけて、長期的に育てていくものです。しかし宮古島では、Webを継続的に育てている事業者がまだほとんどいません。だからこそ、今から本気で力を入れて積み上げれば、必ず成果につながります。
              </p>
              <p>
                長く続けるために必要なのは、制作会社へ任せ続けることではなく、AIを使って皆さん自身が更新・改善できる力です。
              </p>
              <strong>制作後も自分たちで育てていけるように、その仕組みづくりと運用を全面的にサポートします。</strong>
            </div>
          </div>
        </section>

        <section className={s.whyWebSection}>
          <div className={s.sectionInner}>
            <div className={s.whyWebIntro}>
              <p className={s.sectionLabel}>WHY WEB, WHY NOW?</p>
              <h2>なぜ今、Webなのか。</h2>
              <div className={s.whyWebStatement}>
                <p>「SNSがあるから、ホームページは必要ない」</p>
                <strong>そうではありません。</strong>
                <h3>多くの事業者がSNSに集中している<br />今だからこそ、Webが差になります。</h3>
              </div>
            </div>

            <div className={s.whyWebContent}>
              <p>
                InstagramやTikTokは、知ってもらうための強い入口です。ただし投稿は流れていき、料金・場所・サービス内容・予約方法を探すには時間がかかります。
              </p>
              <p>
                ホームページは必要な情報を整理し、Google検索から新しいお客様に見つけてもらい、安心して予約・問い合わせできる場所です。SNSで興味を持った人が最後に確認する「事業の公式な受け皿」にもなります。
              </p>
              <div className={s.snsWebFlow}>
                <div><span>SNS</span><strong>知ってもらう</strong><small>写真・動画・日々の発信</small></div>
                <b>＋</b>
                <div><span>WEB</span><strong>信頼してもらう</strong><small>料金・内容・場所を整理</small></div>
                <b>＝</b>
                <div><span>RESULT</span><strong>予約につなげる</strong><small>LINE・問い合わせ・直接予約</small></div>
              </div>
              <p className={s.whyWebConclusion}>
                SNSかWebかではなく、<strong>SNSとWebの両方を持つこと</strong>が、これからの宮古島の集客を強くします。
              </p>
            </div>
          </div>
        </section>

        <section className={s.featuresSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>WHAT WE CAN DO</p>
            <h2>事業に必要な機能を、分かりやすく。</h2>
            <div className={s.featureGrid}>
              {features.map(([icon, label]) => (
                <div className={s.featureCard} key={label}>
                  <span>{icon}</span><strong>{label}</strong>
                </div>
              ))}
            </div>
            <p className={s.smallNote}>※予約・決済など外部サービスを利用する機能は、サービス側の利用料が別途発生する場合があります。</p>
          </div>
        </section>

        <section className={s.meoSection}>
          <div className={s.sectionInner}>
            <div className={s.meoIntro}>
              <div>
                <p className={s.sectionLabel}>LOCAL SEARCH / MEO</p>
                <h2>Googleマップから、<br />来店・予約につなげる。</h2>
              </div>
              <p>
                「宮古島 カフェ」「宮古島 居酒屋」「宮古島 ツアー」のような検索では、ホームページより先にGoogleマップが見られることがあります。特に海外から訪れるお客様は、Googleマップの写真や口コミ、評価を見て、行くお店や参加するツアーを決める傾向があります。MEO対策で店舗情報を整え、日本人にも外国人にも見つけてもらいやすく、安心して来店・問い合わせ・予約へ進める状態をつくります。
              </p>
            </div>
            <div className={s.meoGrid}>
              <article>
                <span>01</span>
                <h3>店舗情報を正しく整える</h3>
                <p>Googleビジネスプロフィールのカテゴリ、営業時間、住所、電話番号、サービス、写真などを整理します。</p>
              </article>
              <article>
                <span>02</span>
                <h3>Web・SNSとつなげる</h3>
                <p>ホームページ・Instagram・LINE・Googleマップの情報と導線を統一し、予約や来店まで迷わせません。</p>
              </article>
              <article>
                <span>03</span>
                <h3>自分たちで育てられる</h3>
                <p>写真や最新情報の投稿、口コミへの返信、データの確認など、継続して改善する方法までお伝えします。</p>
              </article>
            </div>
            <div className={s.meoFlow}>
              <span>Google検索・マップ</span><b>→</b><span>店舗情報・口コミを確認</span><b>→</b><strong>来店・予約</strong>
            </div>
            <p className={s.meoNote}>※順位を保証する施策や、実際の利用者によらない口コミ投稿は行いません。正確な情報と継続的な運用で、選ばれやすい状態を育てます。</p>
          </div>
        </section>

        <section className={s.flowSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>OWN YOUR AUDIENCE</p>
            <h2>予約サイトだけに頼らない<br />自社集客の導線を育てる。</h2>
            <div className={s.flow}>
              <div><span>01</span><strong>Google検索</strong><small>必要な人に見つけてもらう</small></div>
              <b>→</b>
              <div><span>02</span><strong>自社ホームページ</strong><small>料金・魅力・安心感を伝える</small></div>
              <b>→</b>
              <div><span>03</span><strong>直接予約・相談</strong><small>LINE・フォーム・予約へ</small></div>
            </div>
            <div className={s.twoColumns}>
              <article>
                <h3>Webは積み上がる資産</h3>
                <p>SNSの投稿は流れていきますが、Webの記事や検索評価は少しずつ蓄積されます。半年、1年と育てることで、24時間働く営業窓口になります。</p>
              </article>
              <article>
                <h3>インバウンドにも対応</h3>
                <p>日本語・英語・中国語・韓国語など、言語ごとに料金や注意事項を整理。海外のお客様にも正確に伝え、検索から予約までつなげます。</p>
              </article>
            </div>
          </div>
        </section>

        <section className={s.differenceSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>OUR DIFFERENCE</p>
            <h2>AIで効率よく作る。<br /><span>経験で、成果につながる形に仕上げる。</span></h2>
            <p className={s.largeCopy}>
              大手企業のWeb制作や、スタートアップでのアプリ・Webサービス立ち上げ経験をもとに、見た目だけではなく「誰を集め、何を伝え、次に何をしてもらうか」まで設計します。
            </p>
            <div className={s.designPhilosophy}>
              <span>DESIGN WITH INTENT</span>
              <div>
                <h3>デザインと設計に、強くこだわります。</h3>
                <p>
                  なぜこのデザインなのか。なぜこの構成なのか。配色、余白、文字の大きさ、情報の順番、予約ボタンの位置まで、すべて論理的に説明できます。
                  <strong>すべてのデザインには、明確な意図があります。</strong>
                </p>
              </div>
            </div>
            <div className={s.designPoints}>
              {["狙う検索キーワード", "予約までの導線", "情報を見せる順番", "育てるコンテンツ", "アクセスを見た改善"].map((item, index) => (
                <div key={item}><span>0{index + 1}</span>{item}</div>
              ))}
            </div>
            <div className={s.comparison}>
              <article>
                <span>よくあるWeb制作</span>
                <ul>
                  <li>AIに作らせた、誰でも作れるような似たデザイン</li>
                  <li>Webの知識や経験がないが、AI任せで制作</li>
                  <li>きれいなページを作って納品するだけ</li>
                  <li>更新のたびに費用がかかる</li>
                  <li>毎月の管理費・運用費を払い続ける</li>
                  <li>集客や予約導線は別で考える</li>
                  <li>作っても成果につながらない</li>
                  <li>制作会社に依存し続ける</li>
                </ul>
              </article>
              <article className={s.comparisonStrong}>
                <span>このWeb制作</span>
                <ul>
                  <li>AIは道具として使い、経験をもとに品質を判断</li>
                  <li>デザインや構成の意図を、すべて説明できる</li>
                  <li>検索・SNS・予約までまとめて設計</li>
                  <li>運用費・管理費は0円</li>
                  <li>多言語とインバウンドにも対応</li>
                  <li>AIで自分たちで自由に更新できる状態へ全面サポート</li>
                  <li>誰でも操作できる簡単な管理画面を提供</li>
                </ul>
              </article>
            </div>
            <div className={s.promise}>
              <strong>作って終わりにしません。</strong>
              <p>
                私たちが最も大切にしているのは、納品後もお客様自身で長期的に運用し、Webを育て続けられる状態をつくることです。
                文章・写真・ブログ・SEO記事・多言語ページの更新方法から、AIを活用した改善まで丁寧にお伝えします。
                制作会社に依存せず、自分たちで情報を発信し、成果につなげていける体制まで全面的にサポートします。
              </p>
            </div>
          </div>
        </section>

        <section className={s.casesSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>WORKS & EXPERIENCE</p>
            <h2>確かな実績</h2>
            <div className={s.caseGrid}>
              {cases.map((item) => (
                <a className={s.caseCard} key={item.title} href={item.url} target="_blank" rel="noreferrer">
                  <span>{item.tag}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <strong>{item.result}</strong>
                  <em>サイトを見る →</em>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className={s.photoSection}>
          <div className={s.sectionInner}>
            <div className={s.photoBox}>
              <div>
                <p className={s.sectionLabel}>PHOTO & MOVIE</p>
                <h2>写真・映像制作も<br />まとめて対応できます。</h2>
              </div>
              <p>店舗・施設・人物・商品・サービス撮影、ドローン撮影、Web用動画素材まで。サイト全体の見せ方を考えながら、一貫して制作できます。<small>※撮影は別途料金です。</small></p>
            </div>
          </div>
        </section>

        <section className={s.productionFlowSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>PRODUCTION FLOW</p>
            <h2>ホームページ制作の流れ</h2>
            <p className={s.productionFlowLead}>
              ご相談から公開、その後ご自身で更新できる状態になるまで、順番に進めます。
            </p>
            <div className={s.productionFlowList}>
              {[
                {
                  number: "01",
                  title: "現状の課題をヒアリング",
                  text: "事業の課題を伺い、Webで解決できるかを最初に判断します。Web制作では解決できない場合は別の方法をご提案し、無理に制作を勧めることはありません。",
                },
                {
                  number: "02",
                  title: "デザイン案を作成",
                  text: "目的とターゲットに合わせたデザイン案をご提案します。内容を一緒に確認・相談しながら、デザインを確定します。",
                },
                {
                  number: "03",
                  title: "公開スケジュールを確定",
                  text: "必要な写真・文章・機能を整理し、制作開始から公開までのスケジュールを相談して決定します。",
                },
                {
                  number: "04",
                  title: "実装",
                  text: "確定したデザインをもとに、スマホ対応・SEO・問い合わせや予約導線など、必要な機能を実装します。",
                },
                {
                  number: "05",
                  title: "テスト確認・自走できる環境づくり",
                  text: "公開前にテスト環境で実際のページをご確認いただきます。管理画面をお渡しし、AIを使って誰でも簡単に文章・写真・ページを変更できる環境と運用体制を整えます。",
                },
                {
                  number: "06",
                  title: "本番公開",
                  text: "最終確認後、本番環境へ公開します。公開して終わりではなく、ご自身で更新しながら育てられる状態でスタートします。",
                },
              ].map((step) => (
                <article className={s.productionFlowItem} key={step.number}>
                  <span>{step.number}</span>
                  <div><h3>{step.title}</h3><p>{step.text}</p></div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={s.notesSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>IMPORTANT NOTES</p>
            <h2>ご依頼前の注意事項</h2>
            <div className={s.notesGrid}>
              <article className={s.noteCard}>
                <span>01</span>
                <div>
                  <h3>既存ホームページの改善も対応可能です</h3>
                  <p>すでにホームページをお持ちで、「新しくアップデートしたい」「きちんと動かして成果につなげたい」というご相談にも対応します。見た目を変えるだけではなく、構成と導線を再設計し、公開後の運用方法まで考えてアップデートします。</p>
                </div>
              </article>

              <article className={s.noteCard}>
                <span>02</span>
                <div>
                  <h3>納期の目安</h3>
                  <p>シンプルなホームページであれば、ご依頼から1か月以内の納品が可能です。必要な情報や素材が揃い、やり取りとデザイン決定が順調に進んだ場合は、最短1週間ほどで納品できる場合があります。</p>
                </div>
              </article>
            </div>

            <div className={s.additionalCosts}>
              <div className={s.additionalCostsHead}>
                <span>ADDITIONAL COSTS</span>
                <h3>別途費用が発生する場合</h3>
              </div>
              <ul>
                <li><strong>複雑なページ・機能</strong><p>ページ数が多い場合や複雑な機能が必要な場合は、追加費用が発生します。ヒアリング後に内容を整理し、制作前に正確なお見積もりをご提示します。</p></li>
                <li><strong>サーバー維持費</strong><p>構成やアクセス数によって発生する場合があります。アクセス数が非常に多くならない限り、基本的には無料枠内で収まります。</p></li>
                <li><strong>ドメイン取得費</strong><p>独自ドメインをご希望の場合は取得・更新費がかかる場合があります。ドメインに特別なこだわりがなければ、無料で運用できる方法もご提案できます。</p></li>
                <li><strong>予約・決済などの外部サービス</strong><p>予約システム、決済システムなど外部サービスを利用する機能は、別途料金と制作期間が必要です。サービス側の月額費用や決済手数料が発生する場合もあります。</p></li>
              </ul>
            </div>
          </div>
        </section>

        <section className={s.webFaqSection}>
          <div className={s.sectionInner}>
            <p className={s.sectionLabel}>FAQ</p>
            <h2>宮古島のホームページ制作について<br />よくある質問</h2>
            <div className={s.webFaqList}>
              {webProductionFaqs.map((item) => (
                <details className={s.webFaqItem} key={item.question}>
                  <summary>{item.question}</summary>
                  <div className={s.webFaqAnswer}>
                    <p>{item.answer}</p>
                    {item.bullets && (
                      <ul>
                        {item.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                      </ul>
                    )}
                    {item.note && <p className={s.webFaqNote}>{item.note}</p>}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className={s.finalCta}>
          <div>
            <p>MIYAKOJIMA WEB CREATIVE</p>
            <h2>宮古島の事業を伝えるWebを、<br />一緒に育てませんか？</h2>
            <span>キャンペーン価格 200,000円（20万円・残り1社様）／ 通常価格 400,000円（40万円）／ 運用費・管理費 0円</span>
            <Cta placement="web_production_bottom" />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default WebProductionPage;
